import {DatePipe} from "@angular/common";
import {computed, inject, Injectable, signal} from "@angular/core";
import {toObservable} from "@angular/core/rxjs-interop";
import {
  catchError,
  combineLatest,
  distinctUntilChanged,
  map,
  Observable,
  of,
  shareReplay,
  Subject,
  switchMap,
  timer,
} from "rxjs";
import {ApiService} from "src/app/services/api.service";
import {NodeDataService} from "src/app/services/node-data.service";
import {SelectionService} from "src/app/services/selection.service";

@Injectable({
  providedIn: "root",
})
export class IncidentIdService {
  selectionService = inject(SelectionService);
  apiService = inject(ApiService);
  nodeDataService = inject(NodeDataService);
  datePipe = inject(DatePipe);

  // sources
  public reportUuid$ = new Subject<string>();
  private productionLine$ = this.selectionService.currentProductionLine;
  private workstation$ = this.selectionService.currentWorkplace;
  private date$ = timer(0, 1000).pipe(
    map(() => new Date()),
    distinctUntilChanged(
      (prev, curr) => prev.toDateString() === curr.toDateString()
    )
  );
  public incidentId$ = combineLatest([
    toObservable(this.nodeDataService.productionLines),
    this.productionLine$,
    toObservable(this.nodeDataService.workstations),
    this.workstation$,
    this.date$,
  ]).pipe(
    switchMap(
      ([
         productionLines,
         currentProductionLine,
         workstations,
         currentWorkstation,
         date,
       ]) => {
        if (!currentProductionLine || !currentWorkstation) {
          return of("");
        }

        const shortCode = productionLines.find(
          (line) => line.id === currentProductionLine
        )?.shortcode;
        const position = workstations.find(
          (station) => station.id === currentWorkstation
        )?.position;
        const formattedDate = this.datePipe.transform(date, "YYMMdd", "de-DE");

        return this.getCurrentIncidentNumber(currentWorkstation).pipe(
          map((number) => {
            if (
              !shortCode ||
              position === undefined ||
              !formattedDate ||
              number === undefined
            ) {
              return "";
            }

            return `${shortCode}-${(position + 1)
              .toString()
              .padStart(3, "0")}-${formattedDate}-${(number + 1)
              .toString()
              .padStart(3, "0")}`;
          })
        );
      }
    ),
    catchError((error) => {
      console.error("Error in incidentId stream:", error);
      return of("");
    }),
    shareReplay(1)
  );

  // state
  private state = signal({
    incidentId: "",
    reportUuid: "",
  });
  // selector
  incidentId = computed(() => this.state().incidentId);
  reportUuid = computed(() => this.state().reportUuid);

  constructor() {
    this.incidentId$.subscribe((id) =>
      this.state.update((state) => ({
        ...state,
        incidentId: id,
      }))
    );

    this.reportUuid$.subscribe((uuid) =>
      this.state.update((state) => ({
        ...state,
        reportUuid: uuid,
      }))
    );
  }

  getCurrentIncidentNumber(workstation: string): Observable<number> {
    return this.apiService.getCurrentIncidentNumber(workstation);
  }
}
